import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';

import {ScrollStackedChart} from '@pb/kit-component-chart-v2';
import {getWidthOfText} from '@pb/kit-util-get-width-of-text';

import * as styles from './user-history-chart.module.styl';

import type {HistoryChartData} from './chart-data';
import type {CommonEventsConfig, ScrollStackedChartConfig} from '@pb/kit-component-chart-v2';
import type {LegendItemClickedEvent} from '@pb/kit-component-chart-v2/dist/common-types/generated-event-types/events';

type Props = {
    config: ScrollStackedChartConfig,
    showTotal?: boolean,
    dataSourceProps: HistoryChartData,
    onLegendItemClicked: LegendItemClickedEvent,
};
type HoveredColumn = {index: number, x: number};
const number = new Intl.NumberFormat('en-US');
const AXIS_AND_MARGINS_WIDTH = 80;
const LABEL_GAP = 16;
const MAX_VISIBLE_COLUMNS = 30;

export const HistoryChart = ({config, dataSourceProps, onLegendItemClicked, showTotal = true}: Props) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const pointerX = useRef<number | null>(null);
    const lastIndex = useRef<number | null>(null);
    const [hovered, setHovered] = useState<HoveredColumn | null>(null);
    const [tooltipLeft, setTooltipLeft] = useState(0);
    const [containerWidth, setContainerWidth] = useState(0);
    useLayoutEffect(() => {
        const container = containerRef.current;
        if (!container) {
            return;
        }
        setContainerWidth(container.clientWidth);
        const observer = new ResizeObserver(([entry]) => setContainerWidth(entry.contentRect.width));
        observer.observe(container);
        return () => observer.disconnect();
    }, []);
    const widestLabel = useMemo(() => Math.max(0, ...dataSourceProps.categories[0].category.map(({label}) =>
        getWidthOfText(label, {
            fontName: config.baseFont,
            fontSize: config.baseFontSize,
        }),
    )), [dataSourceProps.categories, config.baseFont, config.baseFontSize]);
    const responsiveConfig = useMemo(() => ({
        ...config,
        numVisiblePlot: Math.min(
            dataSourceProps.categories[0].category.length,
            MAX_VISIBLE_COLUMNS,
            Math.max(1, Math.floor((containerWidth - AXIS_AND_MARGINS_WIDTH) / (widestLabel + LABEL_GAP))),
        ),
    }), [config, dataSourceProps.categories, containerWidth, widestLabel]);
    const showColumn = useCallback((index: number) => {
        const x = pointerX.current;
        if (x === null || !dataSourceProps.categories[0].category[index]) {
            return;
        }
        lastIndex.current = index;
        setHovered(previous => previous?.index === index ? previous : {index, x});
    }, [dataSourceProps]);
    const events = useMemo<CommonEventsConfig>(() => ({
        legendItemClicked: onLegendItemClicked,
        // FusionCharts clears its native tooltip between plots; keep the React tooltip mounted instead.
        onChangeCrossLine: event => showColumn(event.data.currentIndex),
        dataPlotRollOver: event => showColumn(event.data.dataIndex),
    }), [onLegendItemClicked, showColumn]);

    useEffect(() => {
        lastIndex.current = null;
        setHovered(null);
    }, [dataSourceProps]);

    useLayoutEffect(() => {
        if (hovered && containerRef.current && tooltipRef.current) {
            const width = tooltipRef.current.offsetWidth;
            const left = hovered.x + width + 10 > containerRef.current.clientWidth
                ? hovered.x - width - 10 : hovered.x + 10;
            setTooltipLeft(Math.max(0, left));
        }
    }, [hovered]);

    const trackPointer = (event: React.MouseEvent<HTMLDivElement>) => {
        pointerX.current = event.clientX - event.currentTarget.getBoundingClientRect().left;
        // Use the rendered canvas bounds so axis labels, legend and scrollbar are excluded.
        const canvas = event.currentTarget.querySelector('svg [class$="-canvas"] > rect')?.getBoundingClientRect();
        if (canvas && (event.clientX < canvas.left || event.clientX > canvas.right
            || event.clientY < canvas.top || event.clientY > canvas.bottom)) {
            pointerX.current = null;
            setHovered(null);
        } else if (lastIndex.current !== null && !hovered) {
            showColumn(lastIndex.current);
        }
    };
    const values = hovered ? dataSourceProps.dataset.filter(series => series.initiallyHidden !== '1')
        .map(series => ({...series, value: series.data[hovered.index]?.value ?? 0})) : [];

    return (
        <div
            ref={containerRef}
            className={styles.historyChart}
            onMouseOverCapture={trackPointer}
            onMouseMoveCapture={trackPointer}
            onMouseLeave={() => {
                pointerX.current = null;
                setHovered(null);
            }}>
            {/* FusionCharts caches scroll geometry; recreate it when the visible column count changes. */}
            <ScrollStackedChart
                key={responsiveConfig.numVisiblePlot}
                height={235}
                config={responsiveConfig}
                dataSourceProps={dataSourceProps}
                events={events}
            />
            {hovered ? (
                <div
                    ref={tooltipRef}
                    role="tooltip"
                    className={styles.chartTooltip}
                    style={{transform: `translateX(${tooltipLeft}px)`}}>
                    <div className={styles.tooltip}>
                        <strong>{dataSourceProps.categories[0].category[hovered.index]?.label}</strong>
                        <table>
                            <tbody>
                                {values.map(series => (
                                    <tr key={series.id}>
                                        <td>
                                            <span className={styles.swatch} style={{backgroundColor: series.color}}/>
                                            {series.seriesName}
                                        </td>
                                        <td>{number.format(series.value)}</td>
                                    </tr>
                                ))}
                                {showTotal ? (
                                    <tr className={styles.total}>
                                        <td>Total</td>
                                        <td>{number.format(values.reduce((sum, series) => sum + series.value, 0))}</td>
                                    </tr>
                                ) : null}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : null}
        </div>
    );
};
